import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
	ArrowDown,
	ArrowUp,
	ArrowUpDown,
	Bot,
	Check,
	CheckCircle,
	ChevronRight,
	Download,
	ExternalLink,
	FileText,
	FolderTree,
	Loader2,
	RefreshCw,
	Search,
	Tag,
	X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { BottomSheet, SheetFooterActions } from "@/components/ui/bottom-sheet";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { MobileFilterField, MobileFilters } from "@/components/ui/mobile-filters";
import { PageHeader } from "@/components/ui/page-header";
import { ResizableSheet } from "@/components/ui/resizable-sheet";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/features/auth";
import { CategoryTreeDnd } from "@/features/kategoryzacja/components/category-tree-dnd";
import { CategoryTreeRootAction } from "@/features/kategoryzacja/components/category-tree-root-action";
import {
	type DistinctCountOption,
	DistinctFilterCombobox,
} from "@/features/kategoryzacja/components/distinct-filter-combobox";
import { EnrichmentBatchBanner } from "@/features/kategoryzacja/components/enrichment-batch-banner";
import {
	type Substance,
	SubstancjaFilterCombobox,
} from "@/features/kategoryzacja/components/substancja-filter-combobox";
import { VerifyToggle } from "@/features/kategoryzacja/components/verify-toggle";
import { usePersistedWithSales90d } from "@/features/kategoryzacja/use-persisted-with-sales-90d";
import { AiDescriptionRenderer } from "@/features/products/components/ai-description-renderer";
import {
	buildCategoryTree as buildCategoryOptions,
	CategoryFilterCombobox,
} from "@/features/products/components/category-filter-combobox";
import { ProductCategoryEditor } from "@/features/products/components/product-category-editor";
import { Visible } from "@/features/visibility/visible";
import { useIsBelowMd } from "@/hooks/use-mobile";
import { apiFetch, BASE_URL, getAccessToken } from "@/lib/auth-api";
import { ensureElementVisible } from "@/lib/route-guards";
import { useDebounce } from "@/lib/use-debounce";

export const Route = createFileRoute("/_auth/kartoteka/kategoryzacja")({
	component: KategoryzacjaPage,
	beforeLoad: async ({ context, location }) => {
		await ensureElementVisible(
			context.queryClient,
			"sidebar.kartoteka.kategoryzacja",
			location.pathname,
		);
	},
	validateSearch: (search: Record<string, unknown>) => ({
		product: (search.product as string) || undefined,
	}),
});

// --- Types ---

interface CategoryNode {
	code: string;
	label: string;
	parentCode: string | null;
	level: number;
	sortOrder: number;
}

interface CategoryStats {
	totalProducts: number;
	categorized: number;
	uncategorized: number;
	verified: number;
	unverified: number;
}

interface ProductRow {
	kod07: string;
	nazwa: string;
	ean: string | null;
	producent: string | null;
	primaryCode: string | null;
	primaryLabel: string | null;
	source: string | null;
	confidence: number | null;
	verified: boolean;
	secondaryCount: number;
	rodzaj: "OTC" | "RX" | "RX100" | "BD";
	substancja: string | null;
	// Atrybut "zalegacz" (Phase 3b) — wypelnione tylko w trybie atrybutowym
	// (galaz "30" w drzewie). Dla zwyklych kategorii pozostaja undefined.
	attributePharmaciesCount?: number;
	attributeMaxDniZalegania?: number | null;
	attributeOldestDatzk?: string | null;
	attributeTotalUnits?: number;
}

interface ProductListResponse {
	items: ProductRow[];
	total: number;
}

interface AttributeAggregateRow {
	bloz07: string;
	nazwa: string | null;
	ean: string | null;
	producent: string | null;
	pharmaciesCount: number;
	maxDniZalegania: number | null;
	oldestDatzk: string | null;
	totalZalegajacyStanUnits: number;
}

interface AttributeAggregatesResponse {
	items: AttributeAggregateRow[];
	total: number;
}

interface AttributePharmacyRow {
	idfirm: number;
	pharmacyName: string | null;
	dniZalegania: number | null;
	oldestDatzk: string | null;
	zalegajacyStanUnits: number;
}

interface AttributePharmaciesResponse {
	items: AttributePharmacyRow[];
	total: number;
}

const ATTRIBUTE_LABELS: Record<string, string> = {
	"3002": "ZALEGACZ",
	"3003": "ZALEGACZE - WLASNE",
};

function mapAttributeResponseToProductList(
	resp: AttributeAggregatesResponse,
	attributeCode: string,
): ProductListResponse {
	return {
		total: resp.total,
		items: resp.items.map((r) => ({
			kod07: r.bloz07,
			nazwa: r.nazwa ?? "—",
			ean: r.ean,
			producent: r.producent,
			primaryCode: attributeCode,
			primaryLabel: ATTRIBUTE_LABELS[attributeCode] ?? attributeCode,
			source: "computed",
			confidence: null,
			verified: true,
			secondaryCount: 0,
			rodzaj: "BD" as const,
			substancja: null,
			attributePharmaciesCount: r.pharmaciesCount,
			attributeMaxDniZalegania: r.maxDniZalegania,
			attributeOldestDatzk: r.oldestDatzk,
			attributeTotalUnits: r.totalZalegajacyStanUnits,
		})),
	};
}

interface ProductCategory {
	id: number;
	categoryCode: string;
	categoryLabel: string;
	isPrimary: boolean;
	source: string;
	confidence: number | null;
	verified: boolean;
}

interface ProductDetail {
	kod07: string;
	nazwa: string;
	ean: string | null;
	producent: string | null;
	substancja: string | null;
	postac: string | null;
	dawka: string | null;
	aiDescription: string | null;
	categories: ProductCategory[];
}

interface EnrichmentStatus {
	isRunning: boolean;
	total: number;
	progress: number;
	batches: number;
}

interface LeafletInfo {
	kod07: string;
	sourceType: "urpl" | "web" | "manual" | null;
	sourceUrl: string | null;
	hasPdf: boolean;
	fullText: string | null;
	fetchedAt: string | null;
	urplProductId: number | null;
	aiDescription: string | null;
	descriptionSource: "ai_only" | "leaflet" | "manual" | null;
	aiModel: string | null;
	generatedAt: string | null;
}

type StatusFilter =
	| "all"
	| "categorized"
	| "uncategorized"
	| "verified"
	| "unverified"
	| "with_leaflet";
type RodzajFilter = "all" | "OTC" | "RX" | "RX100" | "BD";
type SortKey =
	| "nazwa"
	| "ean"
	| "primaryCode"
	| "source"
	| "confidence"
	| "verified"
	| "rodzaj"
	| "substancja"
	| "kod07";
type SortDir = "asc" | "desc";

// --- Tree helpers ---

interface TreeNode extends CategoryNode {
	children: TreeNode[];
}

function buildCategoryTree(flat: CategoryNode[]): TreeNode[] {
	const sorted = [...flat].sort((a, b) => a.sortOrder - b.sortOrder);
	const map = new Map<string, TreeNode>();
	const roots: TreeNode[] = [];

	for (const n of sorted) {
		map.set(n.code, { ...n, children: [] });
	}
	for (const n of sorted) {
		const node = map.get(n.code);
		if (!node) continue;
		if (n.parentCode && map.has(n.parentCode)) {
			map.get(n.parentCode)?.children.push(node);
		} else {
			roots.push(node);
		}
	}
	return roots;
}

function filterTree(nodes: TreeNode[], query: string): TreeNode[] {
	if (!query) return nodes;
	const q = query.toLowerCase();
	return nodes
		.map((n) => {
			const childMatches = filterTree(n.children, query);
			const selfMatches = n.label.toLowerCase().includes(q) || n.code.toLowerCase().includes(q);
			if (selfMatches || childMatches.length > 0) {
				return { ...n, children: childMatches.length > 0 ? childMatches : n.children };
			}
			return null;
		})
		.filter(Boolean) as TreeNode[];
}

// --- Query keys ---

/**
 * Product list query key — MUST share the ["categories", ...] prefix with every other
 * query on this page so a single `invalidateQueries({ queryKey: ["categories"] })`
 * after batch verify / classify / primary-assign refetches the list. Using a detached
 * prefix (e.g. "categories-products") silently breaks the invalidation chain and
 * stale rows stay visible under filters like "niezweryfikowane" — issue #48.
 *
 * Exported so the test suite can pin the contract.
 */
export function productListQueryKey(params: {
	category: string | null;
	status: string;
	rodzaj: string;
	substancja: string | null;
	dawka: string | null;
	opakh: string | null;
	search: string;
	sortBy: string;
	sortDir: string;
	withSales90d: boolean;
}) {
	return ["categories", "products", params] as const;
}

const keys = {
	tree: ["categories", "tree"] as const,
	stats: (withSales90d: boolean) => ["categories", "stats", { withSales90d }] as const,
	products: productListQueryKey,
	detail: (kod07: string) => ["categories", "product", kod07] as const,
	enrichStatus: ["admin", "enrich", "status"] as const,
	leaflet: (kod07: string) => ["leaflet", kod07] as const,
};

// --- Main page ---

function AttributePharmaciesPanel({
	query,
}: {
	query: ReturnType<typeof useQuery<AttributePharmaciesResponse>>;
}) {
	if (query.isLoading) {
		return (
			<div className="flex items-center justify-center py-12">
				<Loader2 className="size-5 animate-spin text-muted-foreground" />
			</div>
		);
	}
	if (query.error) {
		return (
			<div className="p-4 text-sm text-destructive">
				Blad ladowania aptek: {(query.error as Error).message}
			</div>
		);
	}
	const items = query.data?.items ?? [];
	if (items.length === 0) {
		return (
			<div className="p-4 text-sm text-muted-foreground">
				Brak aptek dla tego BLOZ-a (atrybut moze byl zsynchronizowany przed ostatnim cronem).
			</div>
		);
	}
	return (
		<div className="space-y-2 p-2">
			<div className="px-2 text-xs text-muted-foreground">
				{items.length} {items.length === 1 ? "apteka" : "aptek"} z atrybutem
			</div>
			<div className="overflow-x-auto rounded border">
				<table className="w-full text-sm">
					<thead className="bg-muted/40 text-xs">
						<tr>
							<th className="px-3 py-2 text-left">Apteka</th>
							<th className="px-3 py-2 text-right">Dni zalegania</th>
							<th className="px-3 py-2 text-left">Najstarsza partia</th>
							<th className="px-3 py-2 text-right">Stan zalegajacy</th>
						</tr>
					</thead>
					<tbody>
						{items.map((row) => (
							<tr key={row.idfirm} className="border-t">
								<td className="px-3 py-2">
									<div className="font-medium">{row.pharmacyName ?? "—"}</div>
									<div className="text-xs text-muted-foreground">idfirm: {row.idfirm}</div>
								</td>
								<td className="px-3 py-2 text-right tabular-nums">{row.dniZalegania ?? "—"}</td>
								<td className="px-3 py-2">
									{row.oldestDatzk ? new Date(row.oldestDatzk).toLocaleDateString("pl-PL") : "—"}
								</td>
								<td className="px-3 py-2 text-right tabular-nums">
									{row.zalegajacyStanUnits.toLocaleString("pl-PL", {
										maximumFractionDigits: 1,
									})}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
}

function KategoryzacjaPage() {
	const queryClient = useQueryClient();
	const { product: productFromUrl } = Route.useSearch();
	const { hasRole, hasAnyRole } = useAuth();
	const canEditCategories = hasAnyRole(["super_admin", "category_manager", "analyst"]);

	// UI state
	const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
	const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
	const [rodzajFilter, setRodzajFilter] = useState<RodzajFilter>("all");
	const [substancjaFilter, setSubstancjaFilter] = useState<string | null>(null);
	const [dawkaFilter, setDawkaFilter] = useState<string | null>(null);
	const [opakhFilter, setOpakhFilter] = useState<string | null>(null);
	const [searchQuery, setSearchQuery] = useState("");
	const debouncedSearch = useDebounce(searchQuery.trim(), 400);
	const [sortBy, setSortBy] = useState<SortKey>("kod07");
	const [sortDir, setSortDir] = useState<SortDir>("asc");
	const [treeSearch, setTreeSearch] = useState("");
	const [selectedProduct, setSelectedProduct] = useState<string | null>(productFromUrl ?? null);
	const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
	const [recentlyVerifiedIds, setRecentlyVerifiedIds] = useState<Set<string>>(new Set());
	const [pendingVerifyIds, setPendingVerifyIds] = useState<Set<string>>(new Set());
	const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
	const [batchDialogOpen, setBatchDialogOpen] = useState(false);
	const [leafletTestOpen, setLeafletTestOpen] = useState(false);
	const [leafletBatchId, setLeafletBatchId] = useState<string | null>(null);
	const [batchCategoryCode, setBatchCategoryCode] = useState("");
	const [withSales90d, setWithSales90d] = usePersistedWithSales90d();
	const sentinelRef = useRef<HTMLDivElement>(null);
	// Telefon: drzewo kategorii (lewy panel desktopu) otwiera sie jako pelny ekran.
	const isBelowMd = useIsBelowMd();
	const [treeSheetOpen, setTreeSheetOpen] = useState(false);

	const PAGE_SIZE = 500;

	// Clear URL search param after opening product from external link.
	// Router API zamiast window.history.replaceState — utrzymuje spójny stan
	// historii TanStack Routera (back/forward, restoration).
	const navigate = Route.useNavigate();
	const initialProductRef = useRef(productFromUrl);
	useEffect(() => {
		if (initialProductRef.current) {
			navigate({ search: { product: undefined }, replace: true });
			initialProductRef.current = undefined;
		}
	}, [navigate]);

	// --- Queries ---

	const treeQuery = useQuery({
		queryKey: keys.tree,
		queryFn: () => apiFetch<CategoryNode[]>("/categories/tree"),
	});

	const statsQuery = useQuery({
		queryKey: keys.stats(withSales90d),
		queryFn: () =>
			apiFetch<CategoryStats>(`/categories/stats${withSales90d ? "?withSales90d=true" : ""}`),
	});

	// Kaskada facetow: kazdy combobox (substancja/dawka/opakh) refetchuje sie gdy
	// zmieni sie KTORYKOLWIEK INNY filtr. Exclude-self gwarantuje ze user dalej
	// widzi caly zbior wlasnej osi (inaczej po wyborze dawki=100mg combobox dawek
	// pokazywalby tylko "100 mg" i nie mozna by zmienic).
	const distinctScope = {
		category: selectedCategory,
		status: statusFilter === "all" ? null : statusFilter,
		rodzaj: rodzajFilter === "all" ? null : rodzajFilter,
		substancja: substancjaFilter,
		dawka: dawkaFilter,
		opakh: opakhFilter,
		// withSales90d propagujemy jako "true" lub null (pomijamy w params jesli OFF),
		// zeby distinct comboboxy (substancje/dawki/opakowania) byly spojne z lista.
		withSales90d: withSales90d ? "true" : null,
	};
	type DistinctScopeKey = keyof typeof distinctScope;
	const buildDistinctParams = (excludeKey: DistinctScopeKey): URLSearchParams => {
		const p = new URLSearchParams();
		for (const [k, v] of Object.entries(distinctScope)) {
			if (k === excludeKey) continue;
			if (v != null && v !== "") p.set(k, String(v));
		}
		return p;
	};

	const substancesQuery = useQuery({
		queryKey: ["categories", "substances", { ...distinctScope, substancja: null }] as const,
		queryFn: () => {
			const qs = buildDistinctParams("substancja").toString();
			return apiFetch<Substance[]>(`/categories/substances${qs ? `?${qs}` : ""}`);
		},
		staleTime: 10 * 60_000,
	});

	const dawkasQuery = useQuery({
		queryKey: ["categories", "dawkas", { ...distinctScope, dawka: null }] as const,
		queryFn: () => {
			const qs = buildDistinctParams("dawka").toString();
			return apiFetch<DistinctCountOption[]>(`/categories/dawkas${qs ? `?${qs}` : ""}`);
		},
		staleTime: 10 * 60_000,
	});

	const opakhsQuery = useQuery({
		queryKey: ["categories", "opakhs", { ...distinctScope, opakh: null }] as const,
		queryFn: () => {
			const qs = buildDistinctParams("opakh").toString();
			return apiFetch<DistinctCountOption[]>(`/categories/opakhs${qs ? `?${qs}` : ""}`);
		},
		staleTime: 10 * 60_000,
	});

	// Atrybut — tylko konkretne LISCIE galezi "30" (np. 3002 ZALEGACZ).
	// Sama galaz "30" (parent) ani niezaimplementowane atrybuty (3001 LIDER)
	// NIE wywoluja attribute mode — backend odrzucalby je z 400.
	// Zrodlo danych: `/categories/attribute-aggregates` (Phase 3b backend),
	// agregat per BLOZ z licznikiem aptek. Standardowy endpoint
	// `/categories/products` zwraca pustke bo product_categories nie ma
	// wpisow dla atrybutow (atrybut jest computed per pharmacy z stancache MV).
	const ATTRIBUTE_LEAF_CODES = ["3002", "3003"] as const;
	const isAttributeBranch =
		selectedCategory != null &&
		(ATTRIBUTE_LEAF_CODES as readonly string[]).includes(selectedCategory);

	const productsQuery = useInfiniteQuery({
		queryKey: productListQueryKey({
			category: selectedCategory,
			status: statusFilter,
			rodzaj: rodzajFilter,
			substancja: substancjaFilter,
			dawka: dawkaFilter,
			opakh: opakhFilter,
			search: debouncedSearch,
			sortBy,
			sortDir,
			withSales90d,
		}),
		queryFn: ({ pageParam = 0 }) => {
			const params = new URLSearchParams();

			// Tryb atrybutowy: agregat per BLOZ, ignoruje status/rodzaj/substancja/dawka/opakh
			// (atrybut jest computed z stancache MV — te filtry nie maja sensu).
			if (isAttributeBranch && selectedCategory) {
				params.set("attributeCode", selectedCategory);
				if (debouncedSearch) params.set("search", debouncedSearch);
				params.set("limit", String(PAGE_SIZE));
				params.set("offset", String(pageParam));
				return apiFetch<AttributeAggregatesResponse>(
					`/categories/attribute-aggregates?${params}`,
				).then((resp) => mapAttributeResponseToProductList(resp, selectedCategory));
			}

			if (selectedCategory) params.set("category", selectedCategory);
			if (statusFilter !== "all") params.set("status", statusFilter);
			if (rodzajFilter !== "all") params.set("rodzaj", rodzajFilter);
			if (substancjaFilter) params.set("substancja", substancjaFilter);
			if (dawkaFilter) params.set("dawka", dawkaFilter);
			if (opakhFilter) params.set("opakh", opakhFilter);
			if (debouncedSearch) params.set("search", debouncedSearch);
			if (withSales90d) params.set("withSales90d", "true");
			params.set("sortBy", sortBy);
			params.set("sortDir", sortDir);
			params.set("limit", String(PAGE_SIZE));
			params.set("offset", String(pageParam));
			return apiFetch<ProductListResponse>(`/categories/products?${params}`);
		},
		getNextPageParam: (lastPage, allPages) => {
			const loaded = allPages.reduce((sum, p) => sum + p.items.length, 0);
			return loaded < lastPage.total ? loaded : undefined;
		},
		initialPageParam: 0,
	});

	const detailQuery = useQuery({
		queryKey: keys.detail(selectedProduct ?? ""),
		queryFn: () => apiFetch<ProductDetail>(`/categories/product/${selectedProduct}`),
		enabled: !!selectedProduct,
	});

	// Drilldown w trybie atrybutowym: lista aptek dla wybranego BLOZ.
	// Tylko gdy w panelu kategoryzacji jest aktywny atrybut (3002 ZALEGACZ).
	const attributePharmaciesQuery = useQuery({
		queryKey: ["categories", "attribute-pharmacies", selectedCategory, selectedProduct] as const,
		queryFn: () => {
			const p = new URLSearchParams();
			p.set("attributeCode", selectedCategory ?? "");
			p.set("bloz07", selectedProduct ?? "");
			return apiFetch<AttributePharmaciesResponse>(`/categories/attribute-pharmacies?${p}`);
		},
		enabled: isAttributeBranch && !!selectedProduct && !!selectedCategory,
	});

	const isSuperAdmin = hasRole("super_admin");
	const enrichQuery = useQuery({
		queryKey: keys.enrichStatus,
		queryFn: () => apiFetch<EnrichmentStatus>("/admin/products/enrich/status"),
		refetchInterval: (query) => (query.state.data?.isRunning ? 3000 : 30000),
		enabled: isSuperAdmin,
	});

	const leafletQuery = useQuery({
		queryKey: keys.leaflet(selectedProduct ?? ""),
		queryFn: () => apiFetch<LeafletInfo>(`/enrichment/leaflet/${selectedProduct}`),
		enabled: !!selectedProduct,
	});

	// --- Mutations ---

	const invalidateAll = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ["categories"] });
	}, [queryClient]);

	// Infinite scroll sentinel
	useEffect(() => {
		const el = sentinelRef.current;
		if (!el) return;
		const observer = new IntersectionObserver(
			(entries) => {
				if (
					entries[0].isIntersecting &&
					productsQuery.hasNextPage &&
					!productsQuery.isFetchingNextPage
				) {
					productsQuery.fetchNextPage();
				}
			},
			{ threshold: 0.1 },
		);
		observer.observe(el);
		return () => observer.disconnect();
	}, [productsQuery.hasNextPage, productsQuery.isFetchingNextPage, productsQuery.fetchNextPage]);

	const classifyMutation = useMutation({
		mutationFn: () =>
			apiFetch<{ started: boolean; reason?: string }>("/admin/categories/classify", {
				method: "POST",
			}),
		onSuccess: (data) => {
			queryClient.invalidateQueries({ queryKey: keys.enrichStatus });
			if (data && !data.started && data.reason) {
				// biome-ignore lint/suspicious/noAlert: Legacy message; replace with a toast when changing this flow.
				alert(data.reason);
			}
		},
	});

	const leafletTestMutation = useMutation({
		mutationFn: () =>
			apiFetch<{ batchId: string; products: number; status: string }>("/enrichment/leaflet-test", {
				method: "POST",
				body: JSON.stringify({ mode: "auto" }),
			}),
		onSuccess: (data) => {
			setLeafletBatchId(data.batchId);
			setLeafletTestOpen(true);
		},
		onError: (error) => {
			console.error("Leaflet test failed:", error);
			// biome-ignore lint/suspicious/noAlert: Legacy message; replace with a toast when changing this flow.
			alert(`Błąd uruchomienia testu: ${error instanceof Error ? error.message : "Nieznany błąd"}`);
		},
	});

	// Dociaganie BRAKOW ulotek + opisow w JEDNYM kroku. batch-leaflets jest incremental
	// (WHERE pl.kod07 IS NULL AND leaflet_attempted_at starsze niz 30 dni) i dla kazdej
	// znalezionej ulotki (URPL/SearXNG) generuje opis Z ULOTKI (description_source=leaflet).
	// Opisy ai_only (z samych metadanych) zostaly wycofane — byly glownie halucynacja.
	const leafletBatchMutation = useMutation({
		mutationFn: () =>
			apiFetch<{ ok: boolean; limit: number }>("/admin/enrichment/batch-leaflets", {
				method: "POST",
				body: JSON.stringify({ limit: 500 }),
			}),
		onSuccess: (data) =>
			toast.success(`Ulotki + opisy: pobieranie wystartowało (do ${data.limit} braków w tle)`),
		onError: (e) => toast.error(`Ulotki: ${e instanceof Error ? e.message : "błąd"}`),
	});

	// Poll leaflet test status
	const leafletTestStatusQuery = useQuery({
		queryKey: ["leaflet-test", leafletBatchId],
		queryFn: () =>
			apiFetch<{
				batchId: string;
				status: string;
				progress: number;
				results: Array<{
					kod07: string;
					nazwa: string;
					leafletFound: boolean;
					source: string | null;
					descriptionGenerated: boolean;
					error: string | null;
				}>;
			}>(`/enrichment/leaflet-test/${leafletBatchId}`),
		enabled: !!leafletBatchId && leafletTestOpen,
		refetchInterval: (query) =>
			query.state.data?.status === "completed" || query.state.data?.status?.startsWith("error")
				? false
				: 2000,
	});

	const batchPrimaryMutation = useMutation({
		mutationFn: ({ kod07s, categoryCode }: { kod07s: string[]; categoryCode: string }) =>
			apiFetch("/categories/batch/primary", {
				method: "POST",
				body: JSON.stringify({ kod07s, categoryCode }),
			}),
		onSuccess: () => {
			invalidateAll();
			setSelectedRows(new Set());
			setBatchDialogOpen(false);
		},
		onError: (e: unknown) => {
			toast.error(
				`Nie udalo sie przypisac kategorii: ${e instanceof Error ? e.message : "blad serwera"}`,
			);
		},
	});

	const batchVerifyMutation = useMutation({
		mutationFn: (kod07s: string[]) =>
			apiFetch("/categories/batch/verify", {
				method: "POST",
				body: JSON.stringify({ kod07s }),
			}),
		onSuccess: () => {
			invalidateAll();
			setSelectedRows(new Set());
		},
		onError: (e: unknown) => {
			toast.error(`Nie udalo sie zweryfikowac: ${e instanceof Error ? e.message : "blad serwera"}`);
		},
	});

	const verifyRowMutation = useMutation({
		mutationFn: (kod07: string) =>
			apiFetch("/categories/batch/verify", {
				method: "POST",
				body: JSON.stringify({ kod07s: [kod07] }),
			}),
		onMutate: async (kod07: string) => {
			setPendingVerifyIds((prev) => {
				const next = new Set(prev);
				next.add(kod07);
				return next;
			});
			setRecentlyVerifiedIds((prev) => {
				const next = new Set(prev);
				next.add(kod07);
				return next;
			});
			await queryClient.cancelQueries({ queryKey: ["categories", "products"] });
			let prevVerified: boolean | undefined;
			queryClient.setQueriesData<{ pages: ProductListResponse[]; pageParams: number[] }>(
				{ queryKey: ["categories", "products"] },
				(old) => {
					if (!old) return old;
					return {
						...old,
						pages: old.pages.map((page) => ({
							...page,
							items: page.items.map((it) => {
								if (it.kod07 !== kod07) return it;
								if (prevVerified === undefined) prevVerified = it.verified;
								return { ...it, verified: true };
							}),
						})),
					};
				},
			);
			return { prevVerified };
		},
		onError: (err, kod07, ctx) => {
			const prev = ctx?.prevVerified;
			if (prev !== undefined) {
				queryClient.setQueriesData<{ pages: ProductListResponse[]; pageParams: number[] }>(
					{ queryKey: ["categories", "products"] },
					(old) => {
						if (!old) return old;
						return {
							...old,
							pages: old.pages.map((page) => ({
								...page,
								items: page.items.map((it) =>
									it.kod07 === kod07 ? { ...it, verified: prev } : it,
								),
							})),
						};
					},
				);
			}
			setRecentlyVerifiedIds((s) => {
				const n = new Set(s);
				n.delete(kod07);
				return n;
			});
			toast.error(
				`Nie udało się zweryfikować: ${err instanceof Error ? err.message : "nieznany błąd"}`,
			);
		},
		onSuccess: (_data, kod07) => {
			// Prefix invalidate — laczy oba warianty stats (withSales90d=true/false).
			queryClient.invalidateQueries({ queryKey: ["categories", "stats"] });
			queryClient.invalidateQueries({ queryKey: keys.detail(kod07) });
		},
		onSettled: (_d, _e, kod07) => {
			setPendingVerifyIds((s) => {
				const n = new Set(s);
				n.delete(kod07);
				return n;
			});
		},
	});

	// --- Derived data ---

	const tree = useMemo(
		() => (treeQuery.data ? buildCategoryTree(treeQuery.data) : []),
		[treeQuery.data],
	);

	const filteredTree = useMemo(() => filterTree(tree, treeSearch), [tree, treeSearch]);

	const categoryComboboxOptions = useMemo(
		() => (treeQuery.data ? buildCategoryOptions(treeQuery.data) : []),
		[treeQuery.data],
	);

	const stats = statsQuery.data;
	const allItems = useMemo(
		() => productsQuery.data?.pages.flatMap((p) => p.items) ?? [],
		[productsQuery.data],
	);
	const totalCount = productsQuery.data?.pages[0]?.total ?? 0;
	const detail = detailQuery.data;
	const enrichStatus = enrichQuery.data;

	// Row selection
	const toggleRow = useCallback((kod07: string) => {
		setSelectedRows((prev) => {
			const next = new Set(prev);
			if (next.has(kod07)) next.delete(kod07);
			else next.add(kod07);
			return next;
		});
	}, []);

	const toggleAllRows = useCallback(() => {
		if (allItems.length === 0) return;
		setSelectedRows((prev) => {
			if (prev.size === allItems.length) return new Set();
			return new Set(allItems.map((p) => p.kod07));
		});
	}, [allItems]);

	// Reset page on filter change
	const handleCategoryClick = useCallback((code: string | null) => {
		setSelectedCategory((prev) => (prev === code ? null : code));
		setSelectedRows(new Set());
		setRecentlyVerifiedIds(new Set());
	}, []);

	const handleStatusChange = useCallback((val: string) => {
		setStatusFilter(val as StatusFilter);
		setSelectedRows(new Set());
		setRecentlyVerifiedIds(new Set());
	}, []);

	const handleRodzajChange = useCallback((val: string) => {
		setRodzajFilter(val as RodzajFilter);
		setSelectedRows(new Set());
		setRecentlyVerifiedIds(new Set());
	}, []);

	const handleSubstancjaChange = useCallback((val: string | null) => {
		setSubstancjaFilter(val);
		setSelectedRows(new Set());
		setRecentlyVerifiedIds(new Set());
	}, []);

	const handleDawkaChange = useCallback((val: string | null) => {
		setDawkaFilter(val);
		setSelectedRows(new Set());
		setRecentlyVerifiedIds(new Set());
	}, []);

	const handleOpakhChange = useCallback((val: string | null) => {
		setOpakhFilter(val);
		setSelectedRows(new Set());
		setRecentlyVerifiedIds(new Set());
	}, []);

	const handleSearchChange = useCallback((val: string) => {
		setSearchQuery(val);
		setSelectedRows(new Set());
		setRecentlyVerifiedIds(new Set());
	}, []);

	// Sort handler — clicking the same column toggles direction, switching columns
	// resets to the natural direction (numeric → desc, text → asc).
	// State updaters stay pure; calling setSortDir from inside the setSortBy updater
	// would double-fire under React 18 StrictMode and leave the dir inconsistent.
	const toggleSort = useCallback(
		(key: SortKey) => {
			if (sortBy === key) {
				setSortDir((d) => (d === "asc" ? "desc" : "asc"));
			} else {
				setSortBy(key);
				setSortDir(key === "confidence" || key === "verified" ? "desc" : "asc");
			}
			setSelectedRows(new Set());
			setRecentlyVerifiedIds(new Set());
		},
		[sortBy],
	);

	const STATUS_LABELS: Record<StatusFilter, string> = {
		all: "Wszystkie",
		categorized: "Skategoryzowane",
		uncategorized: "Bez kategorii",
		verified: "Zweryfikowane",
		unverified: "Niezweryfikowane",
		with_leaflet: "Z ulotką",
	};
	// Kontrolki filtrow budowane raz: ten sam JSX w rzedzie desktopu i w arkuszu
	// filtrow na telefonie (`MobileFilters`).
	const statusSelect = (className: string) => (
		<Select value={statusFilter} onValueChange={handleStatusChange}>
			<SelectTrigger data-testid="status-filter" className={className}>
				<SelectValue />
			</SelectTrigger>
			<SelectContent>
				<SelectItem value="all">Wszystkie</SelectItem>
				<SelectItem value="categorized">Skategoryzowane</SelectItem>
				<SelectItem value="uncategorized">Bez kategorii</SelectItem>
				<SelectItem value="verified">Zweryfikowane</SelectItem>
				<SelectItem value="unverified">Niezweryfikowane</SelectItem>
				<SelectItem value="with_leaflet">Z ulotką</SelectItem>
			</SelectContent>
		</Select>
	);
	const rodzajSelect = (className: string) => (
		<Select value={rodzajFilter} onValueChange={handleRodzajChange}>
			<SelectTrigger className={className}>
				<SelectValue />
			</SelectTrigger>
			<SelectContent>
				<SelectItem value="all">Rodzaj</SelectItem>
				<SelectItem value="OTC">
					<span className="inline-flex items-center gap-1.5">
						<span className="size-2 rounded-full bg-emerald-500" />
						OTC
					</span>
				</SelectItem>
				<SelectItem value="RX">
					<span className="inline-flex items-center gap-1.5">
						<span className="size-2 rounded-full bg-blue-500" />
						RX
					</span>
				</SelectItem>
				<SelectItem value="RX100">
					<span className="inline-flex items-center gap-1.5">
						<span className="size-2 rounded-full bg-violet-500" />
						RX100
					</span>
				</SelectItem>
				<SelectItem value="BD">
					<span className="inline-flex items-center gap-1.5">
						<span className="size-2 rounded-full bg-gray-400" />
						BD
					</span>
				</SelectItem>
			</SelectContent>
		</Select>
	);
	const substancjaPicker = (className: string) => (
		<SubstancjaFilterCombobox
			value={substancjaFilter}
			onChange={handleSubstancjaChange}
			options={substancesQuery.data ?? []}
			loading={substancesQuery.isLoading}
			className={className}
		/>
	);
	const dawkaPicker = (className: string) => (
		<DistinctFilterCombobox
			value={dawkaFilter}
			onChange={handleDawkaChange}
			options={dawkasQuery.data ?? []}
			loading={dawkasQuery.isLoading}
			placeholder="Dawka..."
			searchPlaceholder="Szukaj dawki..."
			clearLabel="Wszystkie dawki"
			className={className}
			testId="dawka-filter"
		/>
	);
	const opakhPicker = (className: string) => (
		<DistinctFilterCombobox
			value={opakhFilter}
			onChange={handleOpakhChange}
			options={opakhsQuery.data ?? []}
			loading={opakhsQuery.isLoading}
			placeholder="Opakowanie..."
			searchPlaceholder="Szukaj opakowania..."
			clearLabel="Wszystkie opakowania"
			className={className}
			testId="opakh-filter"
		/>
	);
	const sales90dToggle = (className?: string) => (
		<button
			type="button"
			onClick={() => setWithSales90d(!withSales90d)}
			className={`${className ?? "ml-auto"} inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs transition-colors ${
				withSales90d
					? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
					: "border-border text-muted-foreground hover:bg-muted/50"
			}`}
			title={
				withSales90d
					? "Pokazuje produkty ze sprzedazą w ciągu ostatnich 90 dni. Kliknij aby pokazać wszystkie."
					: "Pokazuje wszystkie produkty z kartoteki. Kliknij aby zawęzić do obrotowych (90 dni)."
			}
		>
			<span
				className={`inline-block size-2 rounded-full ${withSales90d ? "bg-emerald-500" : "bg-muted-foreground/40"}`}
			/>
			{withSales90d ? "Tylko z obrotem 90d" : "Wszystkie produkty"}
		</button>
	);
	const mobileCategoryPicker = (
		<CategoryFilterCombobox
			options={categoryComboboxOptions}
			value={selectedCategory ?? ""}
			onChange={(v) => handleCategoryClick(v || null)}
			placeholder="Kategoria..."
			className="h-10 w-full text-sm"
		/>
	);
	const selectedCategoryLabel = selectedCategory
		? (categoryComboboxOptions.find((o) => o.value === selectedCategory)?.label ?? selectedCategory)
		: "Wszystkie kategorie";
	const clearMobileFilters = () => {
		setSelectedCategory(null);
		setStatusFilter("all");
		setRodzajFilter("all");
		setSubstancjaFilter(null);
		setDawkaFilter(null);
		setOpakhFilter(null);
		setSelectedRows(new Set());
		setRecentlyVerifiedIds(new Set());
	};

	return (
		<div className="flex flex-col h-full overflow-hidden">
			{/* Header */}
			<div className="p-6 pb-3 space-y-3 max-md:px-3 max-md:pt-3 max-md:pb-2 max-md:space-y-2 max-md:empty:hidden">
				<EnrichmentBatchBanner />
				<PageHeader
					title="Kategoryzacja produktów"
					description="Przypisywanie kategorii biznesowych do produktów"
					actions={
						<div className="flex items-center gap-2 max-md:flex-wrap">
							{enrichStatus?.isRunning && (
								<Badge variant="secondary" className="gap-1.5">
									<Loader2 className="size-3 animate-spin" />
									Klasyfikacja: {enrichStatus.progress}/{enrichStatus.total}
								</Badge>
							)}
							<Visible id="categories.classify.button">
								<Button
									size="sm"
									onClick={() => classifyMutation.mutate()}
									disabled={classifyMutation.isPending || !!enrichStatus?.isRunning}
								>
									{classifyMutation.isPending ? (
										<Loader2 className="size-4 animate-spin mr-1.5" />
									) : (
										<Bot className="size-4 mr-1.5" />
									)}
									Kategoryzuj AI
								</Button>
							</Visible>
							<Visible id="categories.leaflet-test.button">
								<Button
									variant="outline"
									size="sm"
									onClick={() => leafletTestMutation.mutate()}
									disabled={leafletTestMutation.isPending}
								>
									{leafletTestMutation.isPending ? (
										<Loader2 className="size-4 animate-spin mr-1.5" />
									) : (
										<FileText className="size-4 mr-1.5" />
									)}
									Test ulotkowy (10+10)
								</Button>
							</Visible>
							{/* Jeden przycisk: ulotka -> opis z ulotki (incremental, tylko braki).
							    Gate reuse leaflet-test (super_admin-only w prod). */}
							<Visible id="categories.leaflet-test.button">
								<Button
									variant="outline"
									size="sm"
									onClick={() => leafletBatchMutation.mutate()}
									disabled={leafletBatchMutation.isPending}
								>
									{leafletBatchMutation.isPending ? (
										<Loader2 className="size-4 animate-spin mr-1.5" />
									) : (
										<Download className="size-4 mr-1.5" />
									)}
									Dociągnij ulotki i opisy
								</Button>
							</Visible>
						</div>
					}
				>
					{/* Stats bar */}
					{stats && (
						<div className="items-center gap-2 mt-2 flex-wrap hidden sm:flex">
							<Badge variant="outline" className="gap-1">
								Wszystkie: {stats.totalProducts}
							</Badge>
							<Badge
								variant="outline"
								className="gap-1 border-green-500/30 text-green-700 dark:text-green-400"
							>
								<CheckCircle className="size-3" />
								Skategoryzowane: {stats.categorized}
							</Badge>
							<Badge
								variant="outline"
								className="gap-1 border-orange-500/30 text-orange-700 dark:text-orange-400"
							>
								Bez kategorii: {stats.uncategorized}
							</Badge>
							<Badge
								variant="outline"
								className="gap-1 border-blue-500/30 text-blue-700 dark:text-blue-400"
							>
								<Check className="size-3" />
								Zweryfikowane: {stats.verified}
							</Badge>
							<Badge
								variant="outline"
								className="gap-1 border-yellow-500/30 text-yellow-700 dark:text-yellow-400"
							>
								Niezweryfikowane: {stats.unverified}
							</Badge>
						</div>
					)}
				</PageHeader>
			</div>

			{/* Telefon: statystyki z paska naglowka jako jedna zwarta linia. */}
			{isBelowMd && stats && (
				<div
					data-testid="kategoryzacja-mobile-stats"
					className="flex flex-wrap gap-x-3 gap-y-0.5 px-3 pb-2 text-xs text-muted-foreground tabular-nums"
				>
					<span className="text-foreground">Wszystkie {stats.totalProducts}</span>
					<span className="text-status-success-text">Skategoryzowane {stats.categorized}</span>
					<span className="text-status-warning-text">Bez kategorii {stats.uncategorized}</span>
					<span className="text-status-info-text">Zweryfikowane {stats.verified}</span>
					<span>Niezweryfikowane {stats.unverified}</span>
				</div>
			)}

			{/* Content area */}
			<div className="flex flex-1 min-h-0 border-t overflow-hidden">
				{/* Left panel — Category Tree (hidden on mobile) */}
				<div className="w-72 border-r flex-col shrink-0 hidden md:flex">
					<div className="p-2 border-b space-y-2">
						<div className="relative">
							<Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
							<Input
								placeholder="Szukaj kategorii..."
								value={treeSearch}
								onChange={(e) => setTreeSearch(e.target.value)}
								className="pl-8 h-8 text-sm"
							/>
						</div>
						<Visible
							id="page.kategoryzacja.category-tree-root-action"
							label="Dodawanie glownej kategorii"
						>
							<CategoryTreeRootAction canEdit={canEditCategories} />
						</Visible>
					</div>
					<div className="flex-1 overflow-auto min-h-0">
						<div className="p-1">
							<button
								type="button"
								onClick={() => handleCategoryClick(null)}
								className={`w-full text-left px-2.5 py-1.5 rounded text-sm flex items-center gap-2 hover:bg-accent transition-colors ${
									selectedCategory === null
										? "bg-primary/10 text-primary font-medium"
										: "text-muted-foreground"
								}`}
							>
								<FolderTree className="size-3.5" />
								Wszystkie produkty
							</button>
							<CategoryTreeDnd
								roots={filteredTree}
								selected={selectedCategory}
								onSelect={handleCategoryClick}
								expandedCategory={expandedCategory}
								onToggleExpand={setExpandedCategory}
								canEdit={canEditCategories}
								canDragDrop={!treeSearch}
							/>
						</div>
					</div>
				</div>

				{/* Main content — Product table */}
				<div className="flex-1 flex flex-col min-w-0">
					{/* Telefon: wyszukiwarka zostaje nad lista, filtry w chipach i arkuszu,
					    drzewo kategorii (lewy panel desktopu) jako pelny ekran. */}
					{isBelowMd && (
						<div className="px-3 pt-2">
							<div className="relative">
								<Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
								<Input
									placeholder="Szukaj produktu..."
									value={searchQuery}
									onChange={(e) => handleSearchChange(e.target.value)}
									className="pl-8 h-10 text-sm"
								/>
							</div>
						</div>
					)}
					<MobileFilters
						chips={[
							{
								id: "category",
								label: "Kategoria",
								value: selectedCategoryLabel,
								active: selectedCategory != null,
								picker: mobileCategoryPicker,
							},
							{
								id: "status",
								label: "Status",
								value: STATUS_LABELS[statusFilter],
								active: statusFilter !== "all",
								picker: statusSelect("h-10 w-full text-sm"),
							},
							{
								id: "rodzaj",
								label: "Rodzaj",
								value: rodzajFilter === "all" ? "Rodzaj" : rodzajFilter,
								active: rodzajFilter !== "all",
								picker: rodzajSelect("h-10 w-full text-sm"),
							},
							{
								id: "substancja",
								label: "Substancja",
								value: substancjaFilter ?? "Substancja",
								active: substancjaFilter != null,
								picker: substancjaPicker("h-10 w-full text-sm"),
							},
							{
								id: "dawka",
								label: "Dawka",
								value: dawkaFilter ?? "Dawka",
								active: dawkaFilter != null,
								picker: dawkaPicker("h-10 w-full text-sm"),
							},
							{
								id: "opakh",
								label: "Opakowanie",
								value: opakhFilter ?? "Opakowanie",
								active: opakhFilter != null,
								picker: opakhPicker("h-10 w-full text-sm"),
							},
							{
								id: "sales90d",
								label: "Obrót 90 dni",
								value: withSales90d ? "Z obrotem 90d" : "Wszystkie produkty",
								picker: sales90dToggle("h-10 w-full justify-center text-sm"),
							},
						]}
						sheetContent={
							<>
								<MobileFilterField label="Kategoria">{mobileCategoryPicker}</MobileFilterField>
								<MobileFilterField label="Status">
									{statusSelect("h-10 w-full text-sm")}
								</MobileFilterField>
								<MobileFilterField label="Rodzaj">
									{rodzajSelect("h-10 w-full text-sm")}
								</MobileFilterField>
								<MobileFilterField label="Substancja">
									{substancjaPicker("h-10 w-full text-sm")}
								</MobileFilterField>
								<div className="grid grid-cols-2 gap-2">
									<MobileFilterField label="Dawka">
										{dawkaPicker("h-10 w-full text-sm")}
									</MobileFilterField>
									<MobileFilterField label="Opakowanie">
										{opakhPicker("h-10 w-full text-sm")}
									</MobileFilterField>
								</div>
								<MobileFilterField label="Zakres produktów">
									{sales90dToggle("h-10 w-full justify-center text-sm")}
								</MobileFilterField>
								{/* Sortowanie: na telefonie wiekszosc kolumn tabeli jest ukryta,
								    wiec ich naglowki sortujace sa dostepne tutaj. */}
								<div className="grid grid-cols-2 gap-2">
									<MobileFilterField label="Sortuj według">
										<Select
											value={sortBy}
											onValueChange={(v) => {
												if (v !== sortBy) toggleSort(v as SortKey);
											}}
										>
											<SelectTrigger className="h-10 w-full text-sm">
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value="kod07">Kod</SelectItem>
												<SelectItem value="nazwa">Nazwa</SelectItem>
												<SelectItem value="ean">EAN</SelectItem>
												<SelectItem value="substancja">Substancja</SelectItem>
												<SelectItem value="primaryCode">Kategoria</SelectItem>
												<SelectItem value="source">Źródło</SelectItem>
												<SelectItem value="confidence">Pewność</SelectItem>
												<SelectItem value="verified">Status</SelectItem>
											</SelectContent>
										</Select>
									</MobileFilterField>
									<MobileFilterField label="Kierunek">
										<Select
											value={sortDir}
											onValueChange={(v) => {
												if (v !== sortDir) toggleSort(sortBy);
											}}
										>
											<SelectTrigger className="h-10 w-full text-sm">
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value="asc">Rosnąco</SelectItem>
												<SelectItem value="desc">Malejąco</SelectItem>
											</SelectContent>
										</Select>
									</MobileFilterField>
								</div>
							</>
						}
						onClear={clearMobileFilters}
						resultLabel={`Pokaż ${totalCount} produktów`}
						actions={
							<button
								type="button"
								data-testid="kategoryzacja-tree-open"
								onClick={() => setTreeSheetOpen(true)}
								aria-label="Drzewo kategorii"
								className="inline-flex size-9 shrink-0 items-center justify-center rounded-md border border-border bg-card text-muted-foreground"
							>
								<FolderTree className="size-4" />
							</button>
						}
					>
						{/* Filters */}
						<div className="flex flex-wrap items-center gap-2 px-3 sm:px-4 py-2 border-b">
							{/* Mobile category filter (replaces tree) */}
							<div className="md:hidden">
								<CategoryFilterCombobox
									options={categoryComboboxOptions}
									value={selectedCategory ?? ""}
									onChange={(v) => handleCategoryClick(v || null)}
									placeholder="Kategoria..."
									className="h-8 text-sm w-36"
								/>
							</div>
							{statusSelect("w-36 sm:w-48 h-8 text-sm")}
							{rodzajSelect("w-28 sm:w-32 h-8 text-sm")}
							{substancjaPicker("h-8 text-sm w-40 sm:w-56")}
							{dawkaPicker("h-8 text-sm w-32 sm:w-40")}
							{opakhPicker("h-8 text-sm w-36 sm:w-44")}
							<div className="relative flex-1 min-w-[140px] max-w-sm">
								<Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
								<Input
									placeholder="Szukaj produktu..."
									value={searchQuery}
									onChange={(e) => handleSearchChange(e.target.value)}
									className="pl-8 h-8 text-sm"
								/>
							</div>
							{selectedCategory && (
								<Badge variant="secondary" className="gap-1 hidden sm:flex">
									Kategoria: {selectedCategory}
									<button
										type="button"
										onClick={() => handleCategoryClick(null)}
										className="ml-0.5 hover:text-foreground"
									>
										<X className="size-3" />
									</button>
								</Badge>
							)}
							{sales90dToggle()}
							<div className="text-xs text-muted-foreground hidden sm:block">{`${totalCount} produktów`}</div>
						</div>
					</MobileFilters>

					{/* Table */}
					<div className="flex-1 overflow-auto min-h-0">
						<table className="w-full text-sm">
							<thead className="sticky top-0 bg-background border-b z-10">
								<tr className="text-left text-muted-foreground">
									<th className="w-10 px-3 py-2">
										<input
											type="checkbox"
											checked={allItems.length > 0 && selectedRows.size === allItems.length}
											onChange={toggleAllRows}
											className="rounded max-md:size-5"
										/>
									</th>
									<SortableHeader
										label="Nazwa"
										sortKey="nazwa"
										sortBy={sortBy}
										sortDir={sortDir}
										onToggle={toggleSort}
									/>
									<SortableHeader
										label="EAN"
										sortKey="ean"
										sortBy={sortBy}
										sortDir={sortDir}
										onToggle={toggleSort}
										className="w-32 hidden sm:table-cell"
									/>
									<SortableHeader
										label="Substancja"
										sortKey="substancja"
										sortBy={sortBy}
										sortDir={sortDir}
										onToggle={toggleSort}
										className="w-40 hidden md:table-cell"
									/>
									<SortableHeader
										label="Kategoria"
										sortKey="primaryCode"
										sortBy={sortBy}
										sortDir={sortDir}
										onToggle={toggleSort}
										className="w-48 hidden md:table-cell"
									/>
									<SortableHeader
										label="Źródło"
										sortKey="source"
										sortBy={sortBy}
										sortDir={sortDir}
										onToggle={toggleSort}
										className="w-20 hidden lg:table-cell"
									/>
									<SortableHeader
										label="Pewność"
										sortKey="confidence"
										sortBy={sortBy}
										sortDir={sortDir}
										onToggle={toggleSort}
										className="w-20 hidden lg:table-cell"
									/>
									<SortableHeader
										label="Status"
										sortKey="verified"
										sortBy={sortBy}
										sortDir={sortDir}
										onToggle={toggleSort}
										className="w-16 hidden sm:table-cell max-md:table-cell"
									/>
								</tr>
							</thead>
							<tbody>
								{productsQuery.isLoading && (
									<tr>
										<td colSpan={8} className="text-center py-12">
											<Loader2 className="size-5 animate-spin mx-auto text-muted-foreground" />
										</td>
									</tr>
								)}
								{allItems.map((row) => (
									<tr
										key={row.kod07}
										onClick={() => setSelectedProduct(row.kod07)}
										className={`border-b hover:bg-accent/50 cursor-pointer transition-colors ${
											selectedProduct === row.kod07 ? "bg-accent" : ""
										} ${recentlyVerifiedIds.has(row.kod07) ? "opacity-60" : ""}`}
									>
										<td className="px-3 py-2">
											<input
												type="checkbox"
												checked={selectedRows.has(row.kod07)}
												onChange={(e) => {
													e.stopPropagation();
													toggleRow(row.kod07);
												}}
												onClick={(e) => e.stopPropagation()}
												className="rounded max-md:size-5"
											/>
										</td>
										<td className="px-3 py-2">
											<div className="font-medium truncate max-w-md max-md:line-clamp-2 max-md:whitespace-normal max-md:break-words">
												{row.nazwa}
											</div>
											<div className="text-xs text-muted-foreground font-mono">{row.kod07}</div>
											{/* Telefon: kolumna Kategoria jest ukryta, wiec kategoria idzie pod nazwe. */}
											{isBelowMd && row.primaryLabel && (
												<div className="text-xs text-muted-foreground line-clamp-1 break-words">
													{row.primaryCode} · {row.primaryLabel}
												</div>
											)}
										</td>
										<td className="px-2 py-1.5 font-mono text-xs text-muted-foreground hidden sm:table-cell">
											{row.ean || "—"}
										</td>
										<td className="px-3 py-2 hidden md:table-cell">
											<span className="text-xs truncate max-w-40 inline-block">
												{row.substancja || "—"}
											</span>
										</td>
										<td className="px-3 py-2 hidden md:table-cell">
											{row.primaryLabel ? (
												<div className="flex items-center gap-1.5">
													<Badge variant="outline" className="text-[10px] font-mono px-1 py-0">
														{row.primaryCode}
													</Badge>
													<span className="text-xs truncate max-w-28" title={row.primaryLabel}>
														{row.primaryLabel}
													</span>
												</div>
											) : (
												<span className="text-xs text-muted-foreground">—</span>
											)}
										</td>
										<td className="px-3 py-2 hidden lg:table-cell">
											{row.source ? (
												<Badge
													variant={row.source === "ai" ? "secondary" : "outline"}
													className="text-[10px] gap-0.5"
												>
													{row.source === "ai" ? (
														<Bot className="size-2.5" />
													) : (
														<Tag className="size-2.5" />
													)}
													{row.source === "ai" ? "AI" : "Ręczne"}
												</Badge>
											) : (
												<span className="text-xs text-muted-foreground">—</span>
											)}
										</td>
										<td className="px-2 py-1.5 text-xs text-center hidden lg:table-cell">
											{row.confidence != null ? `${Math.round(row.confidence * 100)}%` : "—"}
										</td>
										<td className="px-2 py-1.5 text-center hidden sm:table-cell max-md:table-cell">
											<VerifyToggle
												kod07={row.kod07}
												verified={row.verified}
												hasCategory={row.primaryCode != null}
												onVerify={(k) => verifyRowMutation.mutate(k)}
												disabled={pendingVerifyIds.has(row.kod07)}
											/>
										</td>
									</tr>
								))}
								{allItems.length === 0 && !productsQuery.isLoading && (
									<tr>
										<td colSpan={8} className="text-center py-12 text-muted-foreground">
											Brak produktów spełniających kryteria
										</td>
									</tr>
								)}
							</tbody>
						</table>
						{/* Sentinel for infinite scroll */}
						<div ref={sentinelRef} className="h-8 flex items-center justify-center">
							{productsQuery.isFetchingNextPage && (
								<Loader2 className="size-4 animate-spin text-muted-foreground" />
							)}
						</div>
					</div>

					{/* Status bar */}
					<div className="flex items-center justify-between px-4 py-1.5 border-t text-xs text-muted-foreground">
						<span>
							{allItems.length} / {totalCount} produktów
						</span>
						{productsQuery.hasNextPage && <span>Przewiń w dół aby załadować więcej</span>}
					</div>

					{/* Batch actions bar — tylko dla rol z canEditCategories. Bez tego
					    user bez uprawnien klikal "Przypisz kategorie", dostawal cichy 403
					    (mutation bez onError) i widzial "nic sie nie dzieje" — issue #81. */}
					{selectedRows.size > 0 && canEditCategories && (
						<div className="flex flex-wrap items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 border-t bg-muted/50">
							<span className="text-sm font-medium">Zaznaczono: {selectedRows.size}</span>
							<Button
								size="sm"
								variant="outline"
								className="max-md:h-9"
								onClick={() => setBatchDialogOpen(true)}
							>
								<Tag className="size-3.5 mr-1.5" />
								Przypisz kategorię
							</Button>
							<Button
								size="sm"
								variant="outline"
								className="max-md:h-9"
								onClick={() => batchVerifyMutation.mutate([...selectedRows])}
								disabled={batchVerifyMutation.isPending}
							>
								{batchVerifyMutation.isPending ? (
									<Loader2 className="size-3.5 mr-1.5 animate-spin" />
								) : (
									<CheckCircle className="size-3.5 mr-1.5" />
								)}
								Zweryfikuj wybrane
							</Button>
							<Button
								size="sm"
								variant="ghost"
								className="max-md:h-9"
								onClick={() => setSelectedRows(new Set())}
							>
								<X className="size-3.5 mr-1.5" />
								Odznacz
							</Button>
						</div>
					)}
				</div>
			</div>

			{/* Detail panel */}
			<ResizableSheet
				open={!!selectedProduct}
				onOpenChange={(open) => {
					if (!open) setSelectedProduct(null);
				}}
				title={
					isAttributeBranch
						? `${selectedProduct ?? ""} — apteki z atrybutem`
						: (detail?.nazwa ?? "Szczegóły produktu")
				}
				defaultWidth={560}
			>
				{isAttributeBranch ? (
					<AttributePharmaciesPanel query={attributePharmaciesQuery} />
				) : (
					<>
						{detail && (
							<ProductDetailPanel
								leafletData={leafletQuery.data}
								onRegenerateDescription={(kod07: string) =>
									apiFetch(`/enrichment/leaflet/${kod07}/regenerate`, {
										method: "POST",
									}).then(() => {
										queryClient.invalidateQueries({ queryKey: ["leaflet"] });
										queryClient.invalidateQueries({
											queryKey: ["categories", "product", kod07],
										});
									})
								}
								detail={detail}
							/>
						)}
						{detailQuery.isLoading && (
							<div className="flex items-center justify-center py-12">
								<Loader2 className="size-5 animate-spin text-muted-foreground" />
							</div>
						)}
					</>
				)}
			</ResizableSheet>

			{/* Telefon: drzewo kategorii (lewy panel desktopu) jako pelny ekran —
			    wybor kategorii oraz dodawanie, zmiana nazwy i usuwanie galezi. */}
			{isBelowMd && (
				<BottomSheet
					open={treeSheetOpen}
					onOpenChange={setTreeSheetOpen}
					title="Drzewo kategorii"
					full
					data-testid="kategoryzacja-tree-sheet"
					footer={
						<SheetFooterActions
							onClear={() => handleCategoryClick(null)}
							clearLabel="Wszystkie"
							clearDisabled={selectedCategory === null}
							primaryLabel={`Pokaż ${totalCount} produktów`}
							onPrimary={() => setTreeSheetOpen(false)}
						/>
					}
				>
					<div className="space-y-2 pb-2">
						<div className="relative">
							<Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
							<Input
								placeholder="Szukaj kategorii..."
								value={treeSearch}
								onChange={(e) => setTreeSearch(e.target.value)}
								className="pl-8 h-10 text-sm"
							/>
						</div>
						<Visible
							id="page.kategoryzacja.category-tree-root-action"
							label="Dodawanie glownej kategorii"
						>
							<CategoryTreeRootAction canEdit={canEditCategories} />
						</Visible>
					</div>
					<button
						type="button"
						onClick={() => handleCategoryClick(null)}
						className={`w-full text-left px-2.5 min-h-10 rounded text-sm flex items-center gap-2 hover:bg-accent transition-colors ${
							selectedCategory === null
								? "bg-primary/10 text-primary font-medium"
								: "text-muted-foreground"
						}`}
					>
						<FolderTree className="size-3.5" />
						Wszystkie produkty
					</button>
					<CategoryTreeDnd
						roots={filteredTree}
						selected={selectedCategory}
						onSelect={handleCategoryClick}
						expandedCategory={expandedCategory}
						onToggleExpand={setExpandedCategory}
						canEdit={canEditCategories}
						canDragDrop={false}
					/>
				</BottomSheet>
			)}

			{/* Batch assign dialog */}
			<Dialog open={batchDialogOpen} onOpenChange={setBatchDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Przypisz kategorię do {selectedRows.size} produktów</DialogTitle>
					</DialogHeader>
					<div className="py-4 max-md:px-4">
						<CategoryFilterCombobox
							options={categoryComboboxOptions}
							value={batchCategoryCode}
							onChange={setBatchCategoryCode}
							leavesOnly
							showAll={false}
							placeholder="Wybierz kategorię..."
							className="w-full max-md:h-10"
						/>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setBatchDialogOpen(false)}>
							Anuluj
						</Button>
						<Button
							onClick={() =>
								batchPrimaryMutation.mutate({
									kod07s: [...selectedRows],
									categoryCode: batchCategoryCode,
								})
							}
							disabled={!batchCategoryCode || batchPrimaryMutation.isPending}
						>
							{batchPrimaryMutation.isPending && <Loader2 className="size-4 animate-spin mr-1.5" />}
							Przypisz
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Leaflet test results dialog */}
			<Dialog
				open={leafletTestOpen}
				onOpenChange={(open) => {
					setLeafletTestOpen(open);
					if (!open) {
						queryClient.invalidateQueries({ queryKey: ["leaflet"] });
						queryClient.invalidateQueries({ queryKey: ["categories"] });
					}
				}}
			>
				<DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2">
							Test ulotkowy
							{leafletTestStatusQuery.data?.status === "processing" && (
								<Loader2 className="size-4 animate-spin" />
							)}
							{leafletTestStatusQuery.data?.status === "completed" && (
								<CheckCircle className="size-4 text-green-600" />
							)}
						</DialogTitle>
					</DialogHeader>
					<div className="flex-1 overflow-auto min-h-0 max-md:px-4">
						{leafletTestStatusQuery.data?.results?.length ? (
							<table className="w-full text-sm">
								<thead className="sticky top-0 bg-background border-b">
									<tr className="text-left text-muted-foreground">
										<th className="px-3 py-2 font-medium">Produkt</th>
										<th className="px-3 py-2 font-medium w-20">Ulotka</th>
										<th className="px-3 py-2 font-medium w-20">Źródło</th>
										<th className="px-3 py-2 font-medium w-20">Opis</th>
									</tr>
								</thead>
								<tbody>
									{leafletTestStatusQuery.data.results.map((r) => (
										<tr
											key={r.kod07}
											className="border-b hover:bg-accent/50 cursor-pointer"
											onClick={() => {
												setSelectedProduct(r.kod07);
												setLeafletTestOpen(false);
											}}
										>
											<td className="px-3 py-2">
												<div className="font-medium truncate max-w-sm">{r.nazwa}</div>
												<div className="text-xs text-muted-foreground font-mono">{r.kod07}</div>
											</td>
											<td className="px-3 py-2 text-center">
												{r.leafletFound ? (
													<CheckCircle className="size-4 text-green-600 mx-auto" />
												) : r.error ? (
													<X className="size-4 text-red-500 mx-auto" />
												) : (
													<span className="text-xs text-muted-foreground">—</span>
												)}
											</td>
											<td className="px-3 py-2">
												{r.source ? (
													<Badge variant="outline" className="text-[10px]">
														{r.source === "urpl" ? "URPL" : "Web"}
													</Badge>
												) : (
													<span className="text-xs text-muted-foreground">—</span>
												)}
											</td>
											<td className="px-3 py-2 text-center">
												{r.descriptionGenerated ? (
													<CheckCircle className="size-4 text-green-600 mx-auto" />
												) : (
													<span className="text-xs text-muted-foreground">—</span>
												)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						) : leafletTestStatusQuery.isLoading ? (
							<div className="flex items-center justify-center py-12">
								<Loader2 className="size-5 animate-spin text-muted-foreground" />
							</div>
						) : (
							<div className="text-center py-12 text-muted-foreground">Brak wyników</div>
						)}
					</div>
					{leafletTestStatusQuery.data && (
						<div className="flex items-center justify-between pt-3 border-t text-xs text-muted-foreground max-md:flex-wrap max-md:gap-1 max-md:px-4">
							<span>
								{leafletTestStatusQuery.data.results?.filter((r) => r.leafletFound).length || 0} /{" "}
								{leafletTestStatusQuery.data.results?.length || 0} ulotek znalezionych
							</span>
							<span>
								{leafletTestStatusQuery.data.results?.filter((r) => r.descriptionGenerated)
									.length || 0}{" "}
								opisów wygenerowanych
							</span>
						</div>
					)}
					<DialogFooter>
						{leafletTestStatusQuery.data?.status === "completed" && (
							<Button
								variant="outline"
								size="sm"
								onClick={() => {
									setLeafletBatchId(null);
									leafletTestMutation.mutate();
								}}
							>
								<RefreshCw className="size-3.5 mr-1.5" />
								Uruchom ponownie
							</Button>
						)}
						<Button variant="outline" onClick={() => setLeafletTestOpen(false)}>
							Zamknij
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}

// --- Sortable header ---

function SortableHeader({
	label,
	sortKey,
	sortBy,
	sortDir,
	onToggle,
	className,
}: {
	label: string;
	sortKey: SortKey;
	sortBy: SortKey;
	sortDir: SortDir;
	onToggle: (key: SortKey) => void;
	className?: string;
}) {
	const active = sortBy === sortKey;
	const Icon = active ? (sortDir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
	return (
		<th className={`px-3 py-2 font-medium ${className ?? ""}`}>
			<button
				type="button"
				onClick={() => onToggle(sortKey)}
				className={`inline-flex items-center gap-1 hover:text-foreground transition-colors ${
					active ? "text-foreground" : ""
				}`}
			>
				{label}
				<Icon className={`size-3 ${active ? "text-foreground" : "text-muted-foreground/60"}`} />
			</button>
		</th>
	);
}

// --- Product Detail Panel ---

function ProductDetailPanel({
	detail,
	leafletData,
	onRegenerateDescription,
}: {
	detail: ProductDetail;
	leafletData?: LeafletInfo;
	onRegenerateDescription: (kod07: string) => Promise<void>;
}) {
	const queryClient = useQueryClient();
	const [isRegenerating, setIsRegenerating] = useState(false);
	const [isRefetching, setIsRefetching] = useState(false);

	return (
		<div className="space-y-5 px-4 py-2">
			{/* Product info */}
			<div className="space-y-2">
				<h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
					Informacje o produkcie
				</h3>
				<div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
					<span className="text-muted-foreground">Kod:</span>
					<span className="font-mono font-bold">{detail.kod07}</span>
					<span className="text-muted-foreground">EAN:</span>
					<span className="font-mono">{detail.ean || "—"}</span>
					<span className="text-muted-foreground">Producent:</span>
					<span>{detail.producent || "—"}</span>
					{detail.substancja && (
						<>
							<span className="text-muted-foreground">Substancja:</span>
							<span>{detail.substancja}</span>
						</>
					)}
					{detail.postac && (
						<>
							<span className="text-muted-foreground">Postać:</span>
							<span>{detail.postac}</span>
						</>
					)}
					{detail.dawka && (
						<>
							<span className="text-muted-foreground">Dawka:</span>
							<span>{detail.dawka}</span>
						</>
					)}
				</div>
			</div>

			{/* Źródło opisu */}
			<div className="space-y-3 border-t pt-4">
				<div className="flex items-center justify-between">
					<h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
						Źródło opisu
					</h3>
					<DescriptionSourceBadge
						source={leafletData?.descriptionSource}
						sourceType={leafletData?.sourceType}
					/>
				</div>

				{leafletData?.fullText && (
					<details className="group">
						<summary className="cursor-pointer text-sm font-medium text-muted-foreground hover:text-foreground flex items-center gap-2">
							<ChevronRight className="h-4 w-4 transition-transform group-open:rotate-90" />
							Ulotka producenta
							{leafletData.sourceType && (
								<span className="ml-auto text-xs px-1.5 py-0.5 rounded bg-muted">
									{leafletData.sourceType === "urpl" ? "URPL" : "Web"}
								</span>
							)}
						</summary>
						<div className="mt-2 max-h-[400px] overflow-y-auto rounded border bg-muted/30 p-3 text-xs leading-relaxed whitespace-pre-wrap">
							{leafletData.fullText}
						</div>
						<div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
							{leafletData.fetchedAt && (
								<span>Pobrano: {new Date(leafletData.fetchedAt).toLocaleDateString("pl")}</span>
							)}
							{leafletData.sourceUrl && (
								<a
									href={leafletData.sourceUrl}
									target="_blank"
									rel="noreferrer"
									className="underline inline-flex items-center gap-1"
								>
									Źródło <ExternalLink className="size-3" />
								</a>
							)}
						</div>
					</details>
				)}

				{detail.aiDescription && (
					<details open className="group">
						<summary className="cursor-pointer text-sm font-medium text-muted-foreground hover:text-foreground flex items-center gap-2">
							<ChevronRight className="h-4 w-4 transition-transform group-open:rotate-90" />
							Opis AI
							{leafletData?.aiModel && (
								<span className="ml-auto text-xs text-muted-foreground">{leafletData.aiModel}</span>
							)}
						</summary>
						<AiDescriptionRenderer
							text={detail.aiDescription}
							className="mt-2 rounded border bg-muted/30 p-3 text-sm leading-relaxed space-y-2"
						/>
					</details>
				)}

				<div className="flex gap-2 max-md:flex-wrap">
					{leafletData?.hasPdf && (
						<Button
							variant="outline"
							size="sm"
							onClick={() =>
								(async () => {
									try {
										const resp = await fetch(`${BASE_URL}/enrichment/leaflet/${detail.kod07}/pdf`, {
											headers: { Authorization: `Bearer ${getAccessToken() || ""}` },
										});
										if (!resp.ok) throw new Error("PDF not found");
										const blob = await resp.blob();
										window.open(URL.createObjectURL(blob), "_blank");
									} catch {
										// biome-ignore lint/suspicious/noAlert: Legacy message; replace with a toast when changing this flow.
										alert("Nie udało się otworzyć PDF");
									}
								})()
							}
						>
							<FileText className="size-3.5 mr-1.5" />
							Otwórz PDF
						</Button>
					)}
					{leafletData?.fullText && (
						<Visible id="categories.leaflet.regenerate.button" label="Regeneruj opis ulotki">
							<Button
								variant="outline"
								size="sm"
								onClick={() => {
									setIsRegenerating(true);
									onRegenerateDescription(detail.kod07)
										.catch((e) =>
											// biome-ignore lint/suspicious/noAlert: Legacy message; replace with a toast when changing this flow.
											alert(`Błąd regeneracji: ${e instanceof Error ? e.message : "Nieznany"}`),
										)
										.finally(() => setIsRegenerating(false));
								}}
								disabled={isRegenerating}
							>
								{isRegenerating ? (
									<Loader2 className="size-3.5 mr-1.5 animate-spin" />
								) : (
									<RefreshCw className="size-3.5 mr-1.5" />
								)}
								{isRegenerating ? "Generowanie..." : "Regeneruj opis"}
							</Button>
						</Visible>
					)}
					{(leafletData?.sourceType === "web" ||
						(!leafletData?.fullText && !leafletData?.sourceType)) && (
						<Visible id="categories.leaflet.refetch.button" label="Wyszukaj/refetch ulotki">
							<Button
								variant="outline"
								size="sm"
								onClick={() => {
									setIsRefetching(true);
									apiFetch<{ ok: boolean; source: string }>(
										`/enrichment/leaflet/${detail.kod07}/refetch`,
										{
											method: "POST",
										},
									)
										.then((result) => {
											queryClient.invalidateQueries({ queryKey: ["leaflet"] });
											queryClient.invalidateQueries({
												queryKey: ["categories", "product", detail.kod07],
											});
											if (result.ok) {
												toast.success(
													result.source === "urpl"
														? "Znaleziono ulotkę w URPL"
														: "Znaleziono źródło przez SearXNG",
												);
											} else {
												toast.warning("SearXNG nie znalazł wiarygodnego źródła dla produktu");
											}
										})
										.catch((e) =>
											toast.error(
												`Błąd wyszukiwania: ${e instanceof Error ? e.message : "Nieznany"}`,
											),
										)
										.finally(() => setIsRefetching(false));
								}}
								disabled={isRefetching}
							>
								{isRefetching ? (
									<Loader2 className="size-3.5 mr-1.5 animate-spin" />
								) : (
									<Search className="size-3.5 mr-1.5" />
								)}
								{isRefetching
									? "Szukanie..."
									: leafletData?.sourceType === "web"
										? "Szukaj lepszego źródła"
										: "Wyszukaj ulotkę"}
							</Button>
						</Visible>
					)}
				</div>
			</div>

			<ProductCategoryEditor
				kod07={detail.kod07}
				onChange={() => {
					queryClient.invalidateQueries({ queryKey: ["categories"] });
				}}
			/>
		</div>
	);
}

// --- Description Source Badge ---

function DescriptionSourceBadge({
	source,
	sourceType,
}: {
	source: string | null | undefined;
	sourceType?: string | null;
}) {
	switch (source) {
		case "leaflet":
			return (
				<span
					className={`text-xs px-2 py-0.5 rounded-full ${
						sourceType === "urpl"
							? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
							: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400"
					}`}
				>
					{sourceType === "urpl" ? "Ulotka URPL" : "Ze strony www"}
				</span>
			);
		case "manual":
			return (
				<span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
					Ręczny
				</span>
			);
		case "ai_only":
			return (
				<span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
					Bez źródła
				</span>
			);
		default:
			return (
				<span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
					Brak ulotki
				</span>
			);
	}
}
